import { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Flex,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Spinner,
  useColorModeValue,
  FormErrorMessage,
  useToast,
  SimpleGrid,
  Select,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import {
  apiCreateProducerCode,
  apiGetProducerCode,
  apiUpdateProducerCode,
} from 'api';

import type { APIProducerCodeData } from 'types/api.types';

const ProducerCodeFormView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const isEditMode = !!id;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch producer code data if in edit mode
  const {
    data: producerCodeData,
    isLoading: isLoadingProducerCode,
    error: producerCodeError,
  } = useQuery<APIProducerCodeData>({
    queryKey: ['producer-code', id],
    queryFn: () => apiGetProducerCode(parseInt(id)),
    enabled: isEditMode,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Set form data when producer code data is loaded
  useEffect(() => {
    if (producerCodeData && isEditMode) {
      setFormData({
        name: producerCodeData.name || '',
        is_active: producerCodeData.is_active || true,
      });
    }
  }, [producerCodeData, isEditMode]);

  // Create producer code mutation
  const createProducerCodeMutation = useMutation({
    mutationFn: (data: any) => apiCreateProducerCode(data),
    onSuccess: () => {
      toast({
        title: 'Producer code created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/admin/settings/producer_codes');
    },
    onError: handleApiError,
  });

  // Update producer code mutation
  const updateProducerCodeMutation = useMutation({
    mutationFn: ({
      producerCodeId,
      data,
    }: {
      producerCodeId: number;
      data: any;
    }) => apiUpdateProducerCode(producerCodeId, data),
    onSuccess: () => {
      toast({
        title: 'Producer code updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      // Invalidate the producer code query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['producer-code', id] });
      navigate('/admin/settings/producer_codes');
    },
    onError: handleApiError,
  });

  // Handle API errors
  function handleApiError(error: any) {
    toast({
      title: `Error ${isEditMode ? 'updating' : 'creating'} producer code`,
      description: 'Please check the form for errors',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });

    const formattedErrors: Record<string, string> = {};

    // Check if error has response property (axios error)
    if (error.response && error.response.data) {
      Object.entries(error.response.data).forEach(
        ([field, messages]: [string, any]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            formattedErrors[field] = messages[0];
          } else if (typeof messages === 'string') {
            formattedErrors[field] = messages;
          }
        },
      );
    }
    // Direct error object (not wrapped in response)
    else if (typeof error === 'object') {
      Object.entries(error).forEach(([field, messages]: [string, any]) => {
        if (Array.isArray(messages) && messages.length > 0) {
          formattedErrors[field] = messages[0];
        } else if (typeof messages === 'string') {
          formattedErrors[field] = messages;
        }
      });
    }

    setErrors(formattedErrors);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === 'true', // Convert string to boolean
    }));

    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim()) {
      setErrors({
        ...errors,
        name: 'Name is required',
      });
      return;
    }

    try {
      const producerCodePayload = {
        name: formData.name,
        is_active: formData.is_active,
      };

      if (isEditMode && id) {
        // Update existing producer code
        updateProducerCodeMutation.mutate({
          producerCodeId: parseInt(id),
          data: producerCodePayload,
        });
      } else {
        // Create new producer code
        createProducerCodeMutation.mutate(producerCodePayload);
      }

      queryClient.invalidateQueries({ queryKey: ['producer-code-list'] });
    } catch (error) {
      toast({
        title: `Error ${isEditMode ? 'updating' : 'creating'} producer code`,
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Show loading state
  if (isEditMode && isLoadingProducerCode) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading data...
        </Flex>
      </Card>
    );
  }

  // Show error state if producer code data failed to load (in edit mode)
  if (isEditMode && producerCodeError) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading producer code data. Please try again.
        </Flex>
      </Card>
    );
  }

  const isSubmitting =
    createProducerCodeMutation.isPending ||
    updateProducerCodeMutation.isPending;

  return (
    <form onSubmit={handleSubmit}>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          {isEditMode ? 'Edit Producer Code' : 'Create New Producer Code'}
        </Heading>

        {errors.non_field_errors && (
          <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
            {errors.non_field_errors}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={6}>
          <FormControl isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              variant="main"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Producer Code Name"
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.is_active}>
            <FormLabel>Status</FormLabel>
            <Select
              variant="main"
              name="is_active"
              value={formData.is_active.toString()}
              onChange={handleSelectChange}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
            <FormErrorMessage>{errors.is_active}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <Flex mt={10} justify="flex-end">
          <Button
            variant="outline"
            mr={3}
            onClick={() => navigate('/admin/settings/producer_codes')}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            type="submit"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Updating' : 'Creating'}
          >
            {isEditMode ? 'Update Producer Code' : 'Create Producer Code'}
          </Button>
        </Flex>
      </Card>
    </form>
  );
};

export default ProducerCodeFormView;
